import { useState, useEffect, useCallback } from 'react'
import req from '@sportninja/common/api/request'

export interface CadenceUpdateResult {
  ok: boolean
}

export interface UseGameStartCadenceResult {
  /** The currently-selected cadence (minutes from midnight; 0 = unset/legacy off). */
  value: number
  /**
   * Optimistically set the cadence and persist it to the flight via the same
   * flight-PUT the wizard's date range autosaves through. Resolves with
   * `{ ok }` so the caller can surface UI feedback. A no-flight call updates
   * local state only (nothing to persist) and resolves ok.
   */
  setCadence: (next: number) => Promise<CadenceUpdateResult>
}

/**
 * Owns the flight-level game-start cadence: the absolute clock boundary
 * (minutes from midnight) generated game starts snap UP to. Hydrates from the
 * active flight's persisted value and persists changes back to
 * `PUT /scheduler/flights/{flightId}`. Null/0 = unset (legacy off-grid timing).
 *
 * Logic lives here (not in the view) so the persist contract + hydration are
 * unit-testable in isolation — the component only renders the Select and shows
 * success/error feedback from the resolved result.
 */
export function useGameStartCadence(
  flightId: string | null | undefined,
  persistedCadence: number
): UseGameStartCadenceResult {
  const [value, setValue] = useState<number>(persistedCadence)

  // Re-hydrate when the active flight changes (switch) or its persisted cadence
  // first loads / changes server-side. Keyed on the resolved value so a
  // same-flight refetch returning the SAME cadence is a no-op, not a clobber.
  useEffect(() => {
    setValue(persistedCadence)
  }, [flightId, persistedCadence])

  const setCadence = useCallback(
    async (next: number): Promise<CadenceUpdateResult> => {
      setValue(next) // optimistic
      if (!flightId) {
        return { ok: true }
      }
      try {
        await req(`/scheduler/flights/${flightId}`, {
          method: 'PUT',
          body: JSON.stringify({ game_start_cadence_minutes: next }),
        })
        return { ok: true }
      } catch {
        // Revert to the persisted (backend) value so the Select can never show
        // an unsaved cadence — generation would otherwise use the stale backend
        // value while the UI claims the new one.
        setValue(persistedCadence)
        return { ok: false }
      }
    },
    [flightId, persistedCadence]
  )

  return { value, setCadence }
}
